// Truth Chart client API. The Node server's /api/truthchart routes
// (server/routes/api/TruthChart/truthChartHandler.js) call the Big C API
// with Truth Chart's own key — the browser never talks to that API. Those
// routes are limited to Admin and Executive Team and identify the caller by
// the x-user-id header, so
// each call takes the signed-in user's id. Every helper throws an Error with
// a user-readable message so the view can render it directly.

const asJson = async (response) => {
  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error("The Truth Chart service returned an unexpected response.");
  }
  if (data && data.error) throw new Error(data.error);
  if (!response.ok) {
    throw new Error(`The Truth Chart service returned ${response.status}.`);
  }
  return data;
};

const headersFor = (userId, extra = {}) => ({
  Accept: "application/json",
  "x-user-id": userId || "",
  ...extra,
});

// → [{id, name}] from Bistrack's SalesRep table; id is the SalesRepID.
export const searchReps = async (search, userId) => {
  const response = await fetch(
    `/api/truthchart/reps?search=${encodeURIComponent(search)}`,
    { headers: headersFor(userId) }
  );
  return asJson(response);
};

// Rep type-ahead is served from the site's sales-rep directory
// (getsalesreps, already proxied at /api/getUserID), which also carries each
// rep's title and branch for the dropdown. Fetched once and cached for the
// session — it's ~120 rows.
let repDirectoryPromise = null;
export const searchRepDirectory = async (search) => {
  if (!repDirectoryPromise) {
    repDirectoryPromise = fetch("/api/getUserID")
      .then((response) => response.json())
      .then((data) => data.recordset || data.data || [])
      .catch(() => {
        // Failed fetches aren't cached, so a flaky request retries on the
        // next keystroke.
        repDirectoryPromise = null;
        return [];
      });
  }
  const directory = await repDirectoryPromise;
  const term = search.toLowerCase();
  return directory
    .filter((rep) => (rep.Name || "").toLowerCase().includes(term))
    .slice(0, 15)
    .map((rep) => ({
      id: rep.SalesRepCode,
      name: rep.Name,
      title: rep.JobTitle,
      branch: rep.Branch,
    }));
};

// The report proc's @Rep is SalesRep.SalesRepID, which the directory above
// doesn't carry (SalesRepCode isn't verifiably the same thing). Resolve the
// picked name to its SalesRepID through the Truth Chart rep search at Run
// time.
export const resolveRepId = async (name, userId) => {
  const matches = await searchReps(name, userId);
  if (!Array.isArray(matches) || !matches.length) return null;
  const exact = matches.find(
    (m) => (m.name || "").toLowerCase() === name.toLowerCase()
  );
  return (exact || matches[0]).id;
};

// → array of raw report rows (one per customer × PG2)
export const fetchReport = async ({ repId, fromDate, toDate, userId }) => {
  const response = await fetch("/api/truthchart/report", {
    method: "POST",
    headers: headersFor(userId, { "Content-Type": "application/json" }),
    body: JSON.stringify({
      rep_id: repId,
      from_date: fromDate,
      to_date: toDate,
    }),
  });
  const data = await asJson(response);
  return data.rows || [];
};
